import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { CheckoutPage } from '../components';
import { useShopStore } from '../../../redux/shop';
import { useCustomerStore } from '../../../redux/customers';
import { useToastStore } from '../../../redux/toast';
import { findUnassignedRequiredItem } from '../../../utils/productPresentation';

export const CheckoutPageContainer: React.FC = () => {
    const history = useHistory();
    const {
        cart,
        cartTotal,
        isFetching,
        errorMessage,
        paymentUrl,
        proforma,
        updateCartItem,
        updateCartItemLearner,
        removeFromCart,
        clearCart,
        checkout,
        submitWithoutPayment,
        clearErrors
    } = useShopStore();
    const { profiles, activeCustomer } = useCustomerStore();
    const toastStore = useToastStore();

    useEffect(() => {
        if (errorMessage) {
            toastStore.showError({ message: errorMessage }).then(() => {
                clearErrors();
            });
        }
    }, [errorMessage, toastStore, clearErrors]);

    const handleUpdateQuantity = (id: string, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(id);
        } else {
            updateCartItem(id, quantity);
        }
    };

    const handleRemoveItem = (id: string) => {
        removeFromCart(id);
    };

    const handleUpdateLearner = (id: string, learnerId: number | null) => {
        updateCartItemLearner(id, learnerId);
    };

    const handleClearCart = () => {
        clearCart();
    };

    const handleCheckout = async () => {
        if (!activeCustomer) {
            toastStore.showError({ message: 'No active customer selected' });
            return;
        }

        if (cart.length === 0) {
            toastStore.showError({ message: 'Cart is empty' });
            return;
        }

        const unassigned = findUnassignedRequiredItem(cart);
        if (unassigned) {
            toastStore.showError({ message: `Please choose who "${unassigned.product.name}" is for.` });
            return;
        }

        try {
            const response = await checkout({
                customerId: activeCustomer.id,
                cart: cart
            });
            
            if (response.results.paymentUrl) {
                window.location.href = response.results.paymentUrl;
            } else {
                toastStore.showSuccess({ message: 'Checkout successful!' });
            }
        } catch (error) {
            // Error is handled by useEffect above
        }
    };

    const handleSubmitWithoutPayment = async () => {
        if (!activeCustomer) {
            toastStore.showError({ message: 'No active customer selected' });
            return;
        }
        if (cart.length === 0) {
            toastStore.showError({ message: 'Cart is empty' });
            return;
        }

        const unassigned = findUnassignedRequiredItem(cart);
        if (unassigned) {
            toastStore.showError({ message: `Please choose who "${unassigned.product.name}" is for.` });
            return;
        }

        try {
            const response = await submitWithoutPayment({
                customerId: activeCustomer.id,
                cart: cart,
            });
            const proformaUuid = response.results.proforma.uuid;
            history.push(`/orders/${proformaUuid}`);
        } catch (error) {
            // Error toast handled by useEffect on errorMessage above.
        }
    };

    const company = activeCustomer?.company;

    return (
        <CheckoutPage
            cart={cart}
            profiles={profiles}
            total={cartTotal}
            customer={activeCustomer}
            isFetching={isFetching}
            paymentUrl={paymentUrl}
            proforma={proforma}
            payLaterAllowed={!!company?.shopPayLaterAllowed}
            paymentProviderConfigured={!!company?.shopPaymentProviderConfigured}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onUpdateLearner={handleUpdateLearner}
            onClearCart={handleClearCart}
            onCheckout={handleCheckout}
            onSubmitWithoutPayment={handleSubmitWithoutPayment}
        />
    );
};
